import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LocaleLink } from "@/components/site/LocaleLink";
import { readConsent, saveConsent, trackingDisabled } from "@/lib/analytics";

export const OPEN_CONSENT_EVENT = "goproprojects:open-consent";

/** Consent banner; only shown after hydration and only when no choice was made yet. */
export function CookieConsent() {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!trackingDisabled() && readConsent() === null) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  if (!open) return null;

  const choose = (value: "granted" | "denied") => {
    saveConsent(value);
    setOpen(false);
  };

  return (
    <section
      role="region"
      aria-label={t("consent.label")}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-primary-foreground/15 bg-primary text-primary-foreground shadow-lg"
    >
      <div className="mx-auto flex max-w-[1280px] flex-col gap-4 px-5 py-6 md:flex-row md:items-center md:justify-between md:px-16">
        <p className="max-w-[70ch] text-base leading-7 text-primary-foreground/90">
          {t("consent.text")}{" "}
          <LocaleLink to="/cookiebeleid" className="rounded-[4px] text-secondary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary">
            {t("consent.more")}
          </LocaleLink>
        </p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => choose("granted")}>{t("consent.accept")}</Button>
          <Button variant="secondary" onClick={() => choose("denied")}>{t("consent.decline")}</Button>
        </div>
      </div>
    </section>
  );
}
